import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getAllDataWithFlag, addNewsData, updateNewsData, deleteNewsData } from '../../../../services/CommonService';
import { exportToExcel } from '../../../../Utils/common-Excel';
import { imgURL, news_WIDTH, news_HEIGHT, MAXSIZE_MB } from '../../../Config';

// News Management Component
const NewsManager = () => {
    // State management
    const [newsItems, setNewsItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingItem, setEditingItem] = useState(null);

    // excel download
    const handleDownloadExcel = () => {
        const exportData = filteredItems?.map((item, index) => ({
            'S.No': index + 1,
            Title: item.title,
            Image: item.image_path,
            'Start Date': formatDate(item.start_date),
            'End Date': formatDate(item.end_date),
            'Created on': formatDate(item.created_at),
            'Updated At': formatDate(item.updated_at)
        }));

        exportToExcel(exportData, 'NewsList', 'News', 'News Report', null, null);
    };


    // Form state
    const [formData, setFormData] = useState({
        title: '',
        start_date: '',
        end_date: '',
        image_path: null,
        pdf_path: '',
        is_deleted: 1
    });

    // Fetch news data from API
    useEffect(() => {
        fetchNewsData();
    }, []);

    const fetchNewsData = async () => {
        // setLoading(true);
        try {
            // Replace with your actual API endpoint
            const response = await getAllDataWithFlag(12);
            const data = response?.data?.news?.filter((m) => m.is_deleted == 1);
            data.sort((a, b) => (new Date(b?.start_date) - new Date(a?.start_date)));
            setNewsItems(data);
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || error?.message,
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    // Add a new news item
    const addNewsItem = async () => {
        try {
            const submissionData = new FormData();
            submissionData.append('title', formData.title);
            submissionData.append('start_date', formData.start_date);
            submissionData.append('end_date', formData.end_date);
            if (formData.pdf_path instanceof File) {
                submissionData.append('pdf_path', formData.pdf_path);
            }
            if (formData.image_path instanceof File) submissionData.append('image_path', formData.image_path);
            if (!editingItem) {
                const response = await addNewsData(submissionData);
                if (response.status == 201) {
                    const newItem = response.data;
                    setNewsItems([...newsItems, newItem]);
                    resetForm();
                    setShowModal(false);
                    fetchNewsData();
                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: 'News item has been added successfully.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } else {
                const response = await updateNewsData(submissionData, formData.id);
                if (response.status == 200) {
                    const updatedItem = response.data
                    setNewsItems(newsItems.map(item =>
                        item.id === editingItem.id ? updatedItem : item
                    ));
                    resetForm();
                    setShowModal(false);
                    setEditingItem(null);
                    fetchNewsData();
                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: 'News item has been updated successfully.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }

        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || error?.message,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    // Update an existing news item
    const updateNewsItem = async () => {
        try {
            // Replace with your actual API endpoint
            const submissionData = new FormData();
            submissionData.append('title', formData.title);
            submissionData.append('start_date', formData.start_date);
            submissionData.append('end_date', formData.end_date);
            if (formData.pdf_path instanceof File) {
                submissionData.append('pdf_path', formData.pdf_path);
            }
            if (formData.image_path) submissionData.append('image_path', formData.image_path);
            const response = await updateNewsData(submissionData, formData.id)

            if (response.status == 200) {
                const updatedItem = response.data
                setNewsItems(newsItems.map(item =>
                    item.id === editingItem.id ? updatedItem : item
                ));
                resetForm();
                setShowModal(false);
                setEditingItem(null);
                fetchNewsData();
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'News item has been updated successfully.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || error?.message,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    // Delete a news item
    const deleteNewsItem = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                // Replace with your actual API endpoint
                const response = await deleteNewsData(id);
                if (response.status == 200) {
                    setNewsItems(newsItems.filter(item => item.id !== id));
                    fetchNewsData();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'The news item has been deleted.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (error) {
                console.error('Error deleting news item:', error);
                Swal.fire({
                    title: "Error",
                    text: error?.response?.data?.message || error?.message,
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    // Handle form input changes
    const handleInputChange = async (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            const file = files[0];

            if (!file) return;

            if (name === 'image_path') {
                // ✅ Validate only images
                const validation = await validateImageFile(file, "thumbnail");
                if (!validation.isValid) {
                    Swal.fire({ icon: 'error', title: 'Validation Error', text: validation.error });
                    e.target.value = '';
                    return;
                }
            }

            if (name === 'pdf_path') {
                // ✅ Validate PDF (no image check)
                if (file.type !== "application/pdf") {
                    Swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: 'Only PDF files are allowed.'
                    });
                    e.target.value = '';
                    return;
                }
            }

            setFormData({
                ...formData,
                [name]: file,
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };


    // Form validation
    const validateForm = () => {
        if (!formData.title.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'News is required!'
            });
            return false;
        }

        if (!formData.start_date) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Start date is required!'
            });
            return false;
        }

        if (!formData.end_date) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'End date is required!'
            });
            return false;
        }

        if (new Date(formData.end_date) < new Date(formData.start_date)) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'End date cannot be earlier than start date!'
            });
            return false;
        }

        if (!editingItem && !formData.image_path) {
            Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Please upload an image.' });
            return false;
        }

        return true;
    };

    // Open modal for adding new item
    const openAddModal = () => {
        resetForm();
        setEditingItem(null);
        setShowModal(true);
    };

    // Open modal for editing an item
    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            id: item.id,
            title: item.title,
            start_date: formatDateForInput(item.start_date),
            end_date: formatDateForInput(item.end_date),
            is_deleted: item.is_deleted
        });
        setShowModal(true);
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            title: '',
            start_date: '',
            end_date: '',
            image_path: null
        });
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${('0' + date.getDate()).slice(-2)}-${('0' + (date.getMonth() + 1)).slice(-2)}-${date.getFullYear()}`;
    };

    // Format date for input field
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (editingItem) {
            updateNewsItem();
        } else {
            addNewsItem();
        }
    };

    // Cancel form edit
    const handleCancel = () => {
        if (formData.title || formData.start_date || formData.end_date) {
            Swal.fire({
                title: 'Unsaved Changes',
                text: 'Are you sure you want to close? Your changes will be lost.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, close it',
                cancelButtonText: 'No, keep editing'
            }).then((result) => {
                if (result.isConfirmed) {
                    setShowModal(false);
                }
            });
        } else {
            setShowModal(false);
        }
    };


    const validateImageFile = (file, type = 'image_path') => {
        return new Promise((resolve) => {
            // Check file size
            if (file.size > MAXSIZE_MB) {
                resolve({
                    isValid: false,
                    error: `File size must be less than ${MAXSIZE_MB / (1024 * 1024)} MB`
                });
                return;
            }

            // Check image dimensions
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = function () {
                URL.revokeObjectURL(objectUrl);
                const aspectRatio = this.width / this.height;
                const expectedAspectRatio = news_WIDTH / news_HEIGHT;
                const hasCorrectAspectRatio = Math.abs(aspectRatio - expectedAspectRatio) < 0.02;

                if (this.width < news_WIDTH || this.height < news_HEIGHT) {
                    resolve({
                        isValid: false,
                        error: `Image should be at least ${news_WIDTH} x ${news_HEIGHT} px (16:9). Current: ${this.width} x ${this.height}`
                    });
                } else if (!hasCorrectAspectRatio) {
                    resolve({
                        isValid: false,
                        error: 'Image must use a 16:9 aspect ratio, such as 1200 x 675 px.'
                    });
                } else {
                    resolve({ isValid: true, error: null });
                }
            };

            img.onerror = function () {
                URL.revokeObjectURL(objectUrl);
                resolve({ isValid: false, error: 'Invalid image file' });
            };

            img.src = objectUrl;
        });
    };

    // Filter news items based on search term
    const filteredItems = newsItems.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Calculate pagination
    const indexOfLastItem = currentPage * entriesPerPage;
    const indexOfFirstItem = indexOfLastItem - entriesPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / entriesPerPage);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="container-fluid mt-4">
            <div className="card shadow">
                <div className="card-header adminCardHeader d-flex justify-content-between text-white">
                    <h4 className="mb-0">News</h4>
                    <button
                        className="btn btn-warning btn-sm"
                        onClick={openAddModal}
                    >
                        <i className="bi bi-plus-circle me-1"></i>  Add News
                    </button>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-6">
                            <div className="d-flex justify-content-start align-items-center">
                                <label className="me-2">Search:</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ width: '150px' }}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex justify-content-end align-items-center">
                                <button className="btn btn-success mb-2" onClick={handleDownloadExcel}>
                                    <i className="bi bi-file-earmark-excel-fill me-2"></i>
                                    Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* News Table */}
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="bg-light">
                                <tr>
                                    <th width="50">S.No.</th>
                                    <th width="100">Image</th>
                                    <th>News</th>
                                    <th width="100">Start Date</th>
                                    <th width="100">End Date</th>
                                    <th width="80">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center">Loading...</td>
                                    </tr>
                                ) : currentItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center">No news items found</td>
                                    </tr>
                                ) : (
                                    currentItems.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className='text-center'>{indexOfFirstItem + index + 1}</td>
                                            <td><img src={`${imgURL}news_image/${item.image_path}`} alt="uploaded image" style={{ width: '40px', height: '40px' }} /></td>
                                            <td>{item.title}</td>
                                            <td>{formatDate(item.start_date)}</td>
                                            <td>{formatDate(item.end_date)}</td>
                                            <td>
                                                <div className="d-flex justify-content-around">
                                                    <i
                                                        className="bi bi-pencil-fill text-primary me-2"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => openEditModal(item)}
                                                    ></i>
                                                    <i
                                                        className="bi bi-trash-fill text-danger"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => deleteNewsItem(item.id)}
                                                    ></i>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex justify-content-start align-items-center">
                            <label className="me-2">Items Per Page:</label>
                            <select
                                className="form-select form-select-sm me-4"
                                style={{ width: '60px' }}
                                value={entriesPerPage}
                                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                        <nav>
                            <ul className="pagination pagination-sm">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div className="text-center text-muted mt-3">
                        {filteredItems.length > 0 ? (
                            <small>Showing {filteredItems.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredItems.length)} of {filteredItems.length} entries</small>
                        ) : (
                            <small>No contacts to display</small>
                        )}
                    </div>
                    {/* Add/Edit Modal */}
                    {showModal && (
                        <>
                            <div className="modal show d-block" tabIndex="-1">
                                <div className="modal-dialog">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">
                                                {editingItem ? 'Edit News' : 'Add News'}
                                            </h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={handleCancel}
                                            ></button>
                                        </div>
                                        <div className="modal-body">
                                            <div className="mb-3">
                                              <label htmlFor="image_path" className="form-label required mb-0"> Upload Image<span className="form-text text-xsm ms-2">(16:9 landscape, 1200 × 675 px) </span> </label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    id="image_path"
                                                    name="image_path"
                                                    accept="image/*"
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Attach PDF (optional)</label>
                                                <input
                                                    type="file"
                                                    name="pdf_path"
                                                    accept="application/pdf"
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="title" className="form-label required">News</label>
                                                <textarea
                                                    className="form-control"
                                                    id="title"
                                                    name="title"
                                                    value={formData.title}
                                                    style={{ height: '100px' }}
                                                    onChange={handleInputChange}
                                                    required
                                                ></textarea>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="start_date" className="form-label required">Start Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="start_date"
                                                            name="start_date"
                                                            value={formData.start_date}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="end_date" className="form-label required">End Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="end_date"
                                                            name="end_date"
                                                            value={formData.end_date}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <div className="btn-group" role="group">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={handleCancel}
                                                >
                                                    Close
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-warning"
                                                    onClick={handleSubmit}
                                                >
                                                    {editingItem ? 'Update' : 'Add'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-backdrop fade show"></div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewsManager;